import { useCallback, useMemo } from 'react'
import { css, Theme, useTheme } from "@emotion/react";

import { ReactComponent as InvoiceIcon } from "images/icons/pay-invoice.svg";
import { ReactComponent as CardIcon } from "images/icons/pay-card.svg";
import { isBetween, isOnly, isUp } from "theme/breakpoints";
import useBreakpoint from "core/hooks/useBreakpoint";
import ContentCombine from "core/Components/ContentCombine";
import HeaderTitle from "pages/Components/CartAndCheckout/components/HeaderTitle";
import OptionCard from "pages/Components/CartAndCheckout/components/OptionCard";

const PAY_OPTIONS = [
    {
        label: 'Invoice',
        icon: InvoiceIcon,
    },
    {
        label: 'Credit card',
        icon: CardIcon,
    }
]

type Props = {
    onSelect: (option: string) => void,
    selectedOption: string,
}

const PaymentComponent = ({ onSelect, selectedOption }: Props) => {
    const theme: Theme = useTheme();
    const { breakpoint } = useBreakpoint();

    const [
        invoiceIconHeight,
        invoiceIconWidth,
        creditCardIconHeight,
        creditCardIconWidth,
    ] = useMemo(() => {
        switch(true) {
            case (isUp(breakpoint, 'xxl')):
                return [72, 72, 49, 74];
            case (isOnly(breakpoint, 'xl')):
                return [68, 68, 47, 70];
            case (isBetween(breakpoint, 'md', 'lg')):
                return [59, 59, 40, 61];
            case (isOnly(breakpoint, 'sm')):
                return [50, 50, 34, 52];
            default:
                return [35, 35, 24, 37];
        }
    }, [breakpoint]);

    const onSelection = useCallback((option: string) => {
        if (selectedOption !== option) {
            onSelect(option);
        }
    }, [selectedOption, onSelect]);

    const optionsContainerStyle = css`
        display: flex;
        justify-content: space-between;
        
        > div:first-of-type {
            ${theme.breakpoints.up('3xl')} {
                 margin-right: 60px;
            }

            ${theme.breakpoints.up('xxl')} {
                margin-right: 63px;
            }

            ${theme.breakpoints.up('xl')} {
                margin-right: 34px;
            }

            ${theme.breakpoints.between('md', 'lg')} {
                margin-right: 47px;
            }

            ${theme.breakpoints.up('sm')} {
                margin-right: 44px;
            }

            ${theme.breakpoints.down('xs')} {
                margin-bottom: 17px;
            }
        }

        ${theme.breakpoints.down('md')} {
            margin-top: 9px;
            margin-bottom: 20px;
        }


        ${theme.breakpoints.down('sm')} {
            margin-top: 8px;
            margin-bottom: 12px;
        }

        ${theme.breakpoints.down('xs')} {
            flex-direction: column;
            margin-top: 13px;
            margin-bottom: 11px;
        }
    `;

    const payByStyle = css`
        justify-content: start;
    `;

    const optionLabelStyle = css`
        font-size: 30px;
        line-height: 40px;

        ${theme.breakpoints.down('sm')} {
            font-size: 19px;
            line-height: 26px;
        }
    `;

    return (
        <div css={optionsContainerStyle}>
            {PAY_OPTIONS.map((option, index) => {
                const height = !index ? invoiceIconHeight : creditCardIconHeight;
                const width = !index ? invoiceIconWidth : creditCardIconWidth;
                return (
                    <OptionCard
                        key={option.label}
                        keyValue={option.label}
                        Icon={option.icon}
                        leftBubbleDirection={!!index}
                        iconHeight={height}
                        iconWidth={width}
                        onSelection={() => onSelection(option.label)}
                        isSelected={option.label === selectedOption}
                        optionLabel={<>
                            <ContentCombine
                                Tag={'p'}
                                lineHeightType={'26'}
                                fontSizeType={'26'}
                                css={payByStyle}
                            >
                                Pay {option.label}
                            </ContentCombine>
                            <HeaderTitle css={optionLabelStyle}>{option.label}</HeaderTitle>
                        </>}
                    />
                );
            })}

        </div>
    );
};

export default PaymentComponent;